/**
 * Module dependencies.
 */

import { useDeleteItem } from 'app/hooks/requests/apps-notes/use-delete-item';
import { usePostNotesEditItem } from 'app/hooks/requests/apps-notes/use-post-edit-item';
import { usePostNotesNewItem } from 'app/hooks/requests/apps-notes/use-post-new-folder';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';

/**
 * `Props` type.
 */

type Props = {
  children: React.ReactNode;
};

/**
 * `ContextType` type.
 */

type ContextType = {
  deleteItem: (payload: { id: string }) => Promise<void>;
  postNewItem: (payload: { name: string; type: 'file' | 'folder'; parent_id?: string | null }) => Promise<void>;
  postEditItem: (payload: { id: string; name: string }) => Promise<void>;
  toggleFavorite: (payload: { id: string; favorite: boolean }) => Promise<void>;
};

/**
 * `ActionsContent` context.
 */

const ActionsContent = React.createContext<ContextType>({
  deleteItem: () => Promise.resolve(),
  postNewItem: () => Promise.resolve(),
  postEditItem: () => Promise.resolve(),
  toggleFavorite: () => Promise.resolve()
});

/**
 * Export `ActionsProvider` component.
 *
 * Wraps the notes mutations and surfaces an error toast on failure (previously
 * these failed silently). Content autosave is handled separately in `notes.tsx`
 * via the save-state indicator, so it is intentionally not routed through here.
 */

export function ActionsProvider({ children }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { error } = useToast();
  const { mutateAsync: deleteItem } = useDeleteItem();
  const { mutateAsync: postNewItem } = usePostNotesNewItem();
  const { mutateAsync: postEditItem } = usePostNotesEditItem();

  const withToast = useCallback(
    async <T,>(run: () => Promise<T>, messageKey: string): Promise<T> => {
      try {
        return await run();
      } catch (err) {
        error({ message: translate(messageKey) });

        throw err;
      }
    },
    [error, translate]
  );

  const value = useMemo<ContextType>(
    () => ({
      deleteItem: payload => withToast(() => deleteItem(payload), 'notes.errors.deleteFailed').then(() => undefined),
      postNewItem: payload => withToast(() => postNewItem(payload), 'notes.errors.createFailed').then(() => undefined),
      postEditItem: payload =>
        withToast(() => postEditItem(payload), 'notes.errors.renameFailed').then(() => undefined),
      toggleFavorite: payload =>
        withToast(() => postEditItem(payload), 'notes.errors.favoriteFailed').then(() => undefined)
    }),
    [deleteItem, postEditItem, postNewItem, withToast]
  );

  return <ActionsContent.Provider value={value}>{children}</ActionsContent.Provider>;
}

/**
 * Export `useActionsContent` hook.
 */

export function useActionsContent(): ContextType {
  return React.useContext(ActionsContent);
}
